import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";

const LOGO = "/assets/logo_transparent.png";

const navLinks = [
  { label: "Inicio", href: "#hero" },
  { label: "Servicios", href: "#servicios" },
  { label: "Proyectos", href: "#proyectos" },
  { label: "Contacto", href: "#contacto" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          scrolled
            ? "bg-black/95 backdrop-blur-2xl shadow-[0_4px_30px_rgba(0,0,0,0.4)]"
            : "bg-black/60 backdrop-blur-xl"
        }`}
      >
        <div className="max-w-[1800px] mx-auto px-4 md:px-12 lg:px-24 flex items-center justify-between h-14 md:h-16">
          <a href="#hero" onClick={() => scrollTo("#hero")} data-testid="nav-logo" className="relative group">
            <div className="absolute inset-0 z-10 pointer-events-none">
              <div style={{
                background: "linear-gradient(105deg, transparent 38%, rgba(255,255,255,0.06) 44%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 56%, transparent 62%)",
                backgroundSize: "250% 100%",
                animation: "shimmerSweep 6s ease-in-out infinite",
                position: "absolute", inset: 0,
              }} />
            </div>
            {LOGO && (
              <img
                src={LOGO}
                alt="Merza Soluciones — Z&M Arquitectos"
                className="h-10 md:h-11 w-auto object-contain relative"
              />
            )}
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href)}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className="text-[13px] tracking-wide text-white/40 hover:text-white/90 transition-all duration-300 relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 group-hover:w-full transition-all duration-300 bg-white/30" />
              </button>
            ))}
            <button
              onClick={() => scrollTo("#contacto")}
              data-testid="nav-cta"
              className="text-[12px] tracking-wide px-5 py-2 rounded-full bg-white/10 text-white/80 border border-white/10 hover:bg-white/20 hover:text-white transition-all duration-300 flex items-center gap-1.5"
            >
              Agendar cita <ArrowRight size={11} strokeWidth={2} />
            </button>
          </div>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden text-white/70"
          >
            {menuOpen ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black flex flex-col items-center justify-center gap-7"
            data-testid="mobile-menu"
          >
            <img src={LOGO} alt="Merza Soluciones" className="h-16 mb-4" />
            <div className="w-12 h-px bg-white/10 my-1" />
            {navLinks.map((link, i) => (
              <motion.button
                key={link.href}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                onClick={() => scrollTo(link.href)}
                className="text-3xl font-light text-white/80 tracking-tight"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {link.label}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
